import React, { createContext, useCallback, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import EventNotification from '../interfaces/eventNotification/EventNotification';
import { getNotifications } from '../services/notificationServices/NotificationService';
import { useAuth } from '../firebase/AuthProvider';
import {
    MEETING_INVITATION_STATE_CHANGED,
    MeetingInvitationStateChangedDetail,
} from '../utilities/notifications/meetingInvitationEvents';

interface NotificationState {
    notifications: EventNotification[];
    setNotifications: React.Dispatch<React.SetStateAction<EventNotification[]>>;
    hasUnread: boolean;
    allReadVersion: number;
    addNewNotification: (notification: EventNotification) => void;
    refreshNotification: () => void;
    markAsAllReadNotification: () => void;
    markNotificationAsReadById: (notificationId : string) => void;
}

const NotificationContext = createContext<NotificationState | undefined>(undefined);

export const NotificationStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentUser,currentUserInfo,impersonatedUserId } = useAuth();
    const identity=JSON.stringify([currentUser?.uid,currentUserInfo?.id,currentUserInfo?.organization?.id,impersonatedUserId]);
    const activeIdentityRef=useRef(identity);activeIdentityRef.current=identity;
    const [notificationOwner,setNotificationOwner]=useState(identity);
    const inFlight=useRef<{identity:string;promise:Promise<void>}>();
    const [notifications, setNotifications] = useState<EventNotification[]>([]);
    const [allReadVersion, setAllReadVersion] = useState(0);
    const allReadVersionRef = useRef(0);
    const visibleNotifications=notificationOwner===identity?notifications:[];
    const hasUnread = visibleNotifications.some(n => !n.read);

    const getNotificationsForUser = useCallback((token:string,expectedIdentity:string):Promise<void> => {
        if(expectedIdentity!==activeIdentityRef.current)return Promise.resolve();
        if(inFlight.current?.identity===expectedIdentity)return inFlight.current.promise;
        const flight={identity:expectedIdentity,promise:Promise.resolve() as Promise<void>};
        flight.promise=(async()=>{
            try {
                while(expectedIdentity===activeIdentityRef.current) {
                    const readVersionAtStart=allReadVersionRef.current;
                    const response=await getNotifications(token,1,50);
                    if(expectedIdentity!==activeIdentityRef.current)return;
                    // A fetch begun before Mark all read cannot restore stale unread flags.
                    if(readVersionAtStart!==allReadVersionRef.current)continue;
                    if(response){setNotificationOwner(expectedIdentity);setNotifications(response.filter((item,index)=>response.findIndex(other=>other.id===item.id)===index));}
                    return;
                }
            } catch { /* The next foreground refresh retries without adding notifications or toasts. */ }
            finally {if(inFlight.current===flight)inFlight.current=undefined;}
        })();
        inFlight.current=flight;
        return flight.promise;
    }, []);

    const refreshCurrent=useCallback(async()=>{
        if(!currentUser)return;
        const expectedIdentity=identity;
        try {const token=await currentUser.getIdToken();if(expectedIdentity===activeIdentityRef.current)await getNotificationsForUser(token,expectedIdentity);}catch{/* Keep current same-account evidence on network/token failures. */}
    },[currentUser,identity,getNotificationsForUser]);

    useEffect(()=>{
        let live=true;setNotifications([]);setNotificationOwner(identity);
        const foregroundRefresh=()=>{if(live&&document.visibilityState!=='hidden')void refreshCurrent();};
        foregroundRefresh();
        const timer=window.setInterval(foregroundRefresh,30000);
        window.addEventListener('focus',foregroundRefresh);
        document.addEventListener('visibilitychange',foregroundRefresh);
        return()=>{live=false;window.clearInterval(timer);window.removeEventListener('focus',foregroundRefresh);document.removeEventListener('visibilitychange',foregroundRefresh);};
    },[identity,refreshCurrent]);

    useEffect(() => {
        const handleMeetingInvitationStateChanged = (
            event: Event,
        ) => {
            const detail = (event as CustomEvent<MeetingInvitationStateChangedDetail>).detail;

            if (detail?.notificationId && detail.status) {
                setNotifications((previous) =>
                    previous.map((notification) =>
                        notification.id === detail.notificationId
                            ? {
                                ...notification,
                                allowApproval: false,
                                actionLabel: detail.status,
                            }
                            : notification,
                    ),
                );
                return;
            }

            void refreshCurrent();
        };

        window.addEventListener(
            MEETING_INVITATION_STATE_CHANGED,
            handleMeetingInvitationStateChanged,
        );

        return () => {
            window.removeEventListener(
                MEETING_INVITATION_STATE_CHANGED,
                handleMeetingInvitationStateChanged,
            );
        };
    }, [refreshCurrent]);

    const refreshNotification = () => {void refreshCurrent();};

    const addNewNotification = (notification: EventNotification) => {
        setNotificationOwner(identity);
        setNotifications((prev) => {
            // If a notification with the same ID already exists, do not add it again.
            if (prev.some(n => n.id === notification.id)) {
                return prev;
            }
            // Otherwise, prepend the new notification.
            return [notification, ...prev];
        });
    };

    const markAsAllReadNotification = () =>{
         allReadVersionRef.current += 1;
         setNotifications(prev => prev.map(n => ({ ...n, read: true })));
         setAllReadVersion(allReadVersionRef.current);
    }

    const markNotificationAsReadById = (notificationId: string) => {
        setNotifications(prev =>
            prev.map(n =>
                n.id === notificationId ? { ...n, read: true } : n
            )
        );
    };



    return (
        <NotificationContext.Provider value={{ notifications:visibleNotifications, setNotifications, hasUnread, allReadVersion, addNewNotification, refreshNotification, markAsAllReadNotification, markNotificationAsReadById }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotificationState = (): NotificationState => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotificationState must be used within a NotificationProvider');
    }
    return context;
};
